import cloneDeep from 'lodash/cloneDeep';
import { Layer as ParseLayer, RGB } from '@meitu-graphic/ag-psd';
import { DefaultClientImageLayer } from '@meitu/saas-editor-core-next/es/version-parse/constants';
import {
    ImgCategoryType,
    ImgModeType,
    MaskType,
    StretchOptionType,
    type IImageLayerProps,
    type ILayerProps,
} from '@meitu/saas-editor-core-next/es/version-types';
import { PsdParseWarnType, UploadImage, WarnOrErrorCallbackFunc } from './types';
import {
    canSaveToJpg,
    getImgMaskUrl,
    getUrlByCanvas,
    parseImageGlow,
    parseImageShadow,
    parseImageStroke,
} from './utils';

export const getImageLayer = async (
    stageWidth: number,
    stageHeight: number,
    parentLayer: ParseLayer,
    layer: ParseLayer,
    uploadFile: UploadImage,
    warnOrErrorCallback?: WarnOrErrorCallbackFunc,
    parseNoLimit = false,
    parseLayerCallback?: (originLayer: ParseLayer, resultLayer: ILayerProps) => Promise<ILayerProps | null>,
) => {
    const imageLayer = cloneDeep(DefaultClientImageLayer);

    imageLayer.opacity = (layer.opacity || 1) * (layer.fillOpacity || 1);
    imageLayer.id = layer.id?.toString() || imageLayer.id;
    const { canvas, top = 0, left = 0, opacity = 1, name: title } = layer;

    imageLayer.left = left;
    imageLayer.top = top;
    imageLayer.opacity = opacity * (layer.fillOpacity || 1);
    imageLayer.title = title || '';

    warnOrErrorCallback?.(`解析图片图层：${imageLayer.title}`, PsdParseWarnType.Info);
    imageLayer.parentId = parentLayer.id && parentLayer.id === layer.id ? '' : parentLayer.id?.toString();
    const data = imageLayer.data as IImageLayerProps;
    if (canvas) {
        imageLayer.width = canvas.width;
        imageLayer.height = canvas.height;

        let mineType = 'image/png';
        if (canSaveToJpg(canvas)) {
            mineType = 'image/jpeg';
        }
        data.url = await getUrlByCanvas(imageLayer.title, canvas, uploadFile, mineType);

        data.naturalWidth = canvas.width || 0;
        data.naturalHeight = canvas.height || 0;
    }
    console.log('【mask layer】', layer);
    const maskUrl = await getImgMaskUrl(layer, uploadFile);
    data.stretchOption = StretchOptionType.fill;

    imageLayer.shine = false;

    if (maskUrl) {
        data.maskURL = maskUrl;
        data.maskType = MaskType.imgLayer;
        data.stretchOption = StretchOptionType.cover;
        imageLayer.mode = ImgModeType.imgContainer;
        imageLayer.shine = true;
    }

    if (
        layer.effects &&
        (layer.effects.dropShadow ||
            (layer.effects.outerGlow && layer.effects.outerGlow.enabled) ||
            layer.effects.stroke)
    ) {
        data.effect = {
            effectAlpha: 1,
            glow: parseImageGlow(layer),
            shadow: parseImageShadow(layer),
            stroke: parseImageStroke(layer),
            screenshotUrl: '',
        };
    }

    if (title?.toLowerCase() === 'qrcode' || title === '【二维码】' || title === '二维码') {
        data.stretchOption = StretchOptionType.fill;
        imageLayer.shine = false;
        imageLayer.category = ImgCategoryType.qrCode;
        imageLayer.mode = ImgModeType.none;
    } else if (title?.toLowerCase() === 'hint') {
        data.hint = true;
    }

    if (!parseNoLimit && (imageLayer.width > stageWidth * 2 || imageLayer.height > stageHeight * 2)) {
        warnOrErrorCallback?.(`图片图层${imageLayer.title}：图层宽高大于当前画布尺寸2倍`, PsdParseWarnType.Error);
    }

    if (imageLayer.width <= 0 || imageLayer.height <= 0) {
        if (parseNoLimit) {
            return null;
        }
        warnOrErrorCallback?.(`图片图层${imageLayer.title}：宽高不能小于0`, PsdParseWarnType.Error);
    }

    if (!(imageLayer.data as IImageLayerProps).url) {
        if (parseNoLimit) {
            return null;
        }
        warnOrErrorCallback?.(`图片图层${imageLayer.title}： 解析不到图片，url是空的`, PsdParseWarnType.Error);
    }

    if (parseLayerCallback) {
        const newLayer = await parseLayerCallback(layer, imageLayer);
        return newLayer;
    }
    return imageLayer;
};
